import type {
  PatientRecord,
  PfAssignmentStatus,
  PfPatientFormsBundle,
  PfSyncStatus,
  PiPatientInsuranceBundle,
  PcCommDetail,
  PcCommStatus,
  PcPatientCommsBundle,
  PcV2Bundle,
  PcV2Channel,
  PtDirectoryPatient,
  TpPatientTreatmentBundle,
} from '@elva/ui';
import { pfFormsStats } from '@elva/ui';
import type {
  ProducerOutputs,
  PatientProfile,
  PatientRosterRecord,
  PatientRosterSummary,
  PatientSearchResult,
  PatientsSearchResponse,
  PatientCommEvent,
  PatientCommEventDetail,
  PatientCommsResponse,
  PatientFormsResponse,
  ClinicalCodeWire,
  ClinicalWireItem,
  PatientProfileFamilyMemberWire,
  PatientProfileHistoryRowWire,
} from '../api/types';
import { logPatientWire } from '../debug/logPatientWire';
import type { InsuranceResponse, ClaimsResponse } from '../../insurance/api/types';
import type { TpiCheatsheet } from '../../tpi/api/types';

type PcChannel = PcPatientCommsBundle['stats'][0]['channel'];

function initials(name: string | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0]![0] ?? ''}${parts[1]![0] ?? ''}`.toUpperCase();
  return (parts[0]?.slice(0, 2) ?? '??').toUpperCase();
}

function splitName(name: string | undefined): { first: string; last: string } {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return { first: parts[0] ?? 'Patient', last: '' };
  return { first: parts[0]!, last: parts.slice(1).join(' ') };
}

function resolveProfileName(profile: PatientProfile): string {
  if (profile.name?.trim()) return profile.name.trim();
  if (profile.legal_name?.trim()) return profile.legal_name.trim();
  if (profile.preferred_name?.trim()) return profile.preferred_name.trim();
  const parts = [profile.first_name, profile.last_name].filter((part) => part?.trim());
  if (parts.length) return parts.join(' ').trim();
  return 'Patient';
}

function centsToDollars(cents: number | undefined): number {
  return cents != null ? Math.round(cents / 100) : 0;
}

function normalizeWireLabel(item: string | ClinicalCodeWire | null | undefined): string | null {
  if (typeof item === 'string') {
    const trimmed = item.trim();
    return trimmed || null;
  }
  if (item && typeof item === 'object') {
    const label = item.label?.trim();
    if (label) return label;
    const code = item.code?.trim();
    if (code) return code;
    if (item.id != null) return String(item.id);
  }
  return null;
}

function normalizeWireLabels(items: ClinicalWireItem[] | undefined): string[] {
  return (items ?? [])
    .map(normalizeWireLabel)
    .filter((item): item is string => item != null);
}

function resolveConversationStarters(
  profile: PatientProfile,
  outputs?: ProducerOutputs,
): string[] {
  const fromOutputs = outputs?.conversation_starters?.items;
  if (fromOutputs?.length) return normalizeWireLabels(fromOutputs);

  const fromProfile = profile.conversation_starters;
  if (Array.isArray(fromProfile)) return normalizeWireLabels(fromProfile);
  if (fromProfile && typeof fromProfile === 'object' && 'items' in fromProfile) {
    return normalizeWireLabels(fromProfile.items);
  }
  return [];
}

function adaptFamilyMembers(members: PatientProfileFamilyMemberWire[] | null | undefined): PatientRecord['family'] {
  return (members ?? [])
    .map((member) => {
      const name = normalizeWireLabel(member.name) ?? 'Unknown';
      return {
        name,
        rel: member.relationship ?? member.rel ?? '',
        status: member.status ?? '',
        overdue: member.overdue ?? false,
      };
    })
    .filter((member) => member.name !== 'Unknown' || member.rel || member.status);
}

function adaptComfort(
  comfort: PatientProfile['comfort'],
): PatientRecord['comfort'] {
  return (comfort ?? [])
    .map((entry) => {
      if (Array.isArray(entry) && entry.length >= 2) {
        return [String(entry[0]), String(entry[1])] as [string, string];
      }
      if (entry && typeof entry === 'object' && !Array.isArray(entry)) {
        const row = entry as { label?: string; value?: string; topic?: string; note?: string };
        const topic = row.topic ?? row.label ?? '';
        const note = row.note ?? row.value ?? '';
        if (topic || note) return [topic, note] as [string, string];
      }
      return null;
    })
    .filter((entry): entry is [string, string] => entry != null);
}

function formatMoney(cents: number | undefined): string {
  if (cents == null) return '$0';
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function formatDate(iso: string | undefined): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
}

function formatProcedureField(raw: string | ClinicalCodeWire | undefined): string {
  if (raw == null) return '';
  if (typeof raw === 'string') return raw.trim();
  const code = raw.code?.trim();
  const label = raw.label?.trim();
  if (code && label) return `${code} · ${label}`;
  return label ?? code ?? '';
}

function formatProviderField(raw: string | ClinicalCodeWire | undefined): string {
  if (raw == null) return '';
  if (typeof raw === 'string') return raw.trim();
  return raw.label?.trim() ?? raw.code?.trim() ?? '';
}

function readHistoryProductionDollars(row: PatientProfileHistoryRowWire): number {
  const cents = row.prod_cents ?? row.prodCents ?? row.production_cents ?? row.productionCents;
  if (cents != null) return Math.round(cents / 100);
  const dollars = row.production ?? row.prod;
  if (dollars != null) return Math.round(Number(dollars));
  return 0;
}

function resolveHistoryDate(row: PatientProfileHistoryRowWire): string {
  return formatDate(row.date ?? row.service_date ?? row.serviceDate);
}

function formatVisitStatus(status: string | undefined): string {
  const normalized = (status ?? 'unknown').trim().replace(/_/g, ' ');
  if (!normalized) return 'Unknown';
  return normalized.charAt(0).toUpperCase() + normalized.slice(1).toLowerCase();
}

function adaptVisitHistory(profile: PatientProfile): PatientRecord['history'] {
  const rows = profile.history ?? profile.visit_history ?? [];
  const adapted = rows.map((row) => ({
    date: resolveHistoryDate(row),
    proc: formatProcedureField(row.proc ?? row.procedure ?? row.procedure_name ?? row.procedureName),
    prov: formatProviderField(row.prov ?? row.provider ?? row.provider_name ?? row.providerName),
    prod: readHistoryProductionDollars(row),
    status: formatVisitStatus(row.status),
  }));
  logPatientWire('adapted visit history', { raw: rows, adapted });
  return adapted;
}

/** Photo URL from whichever alias the wire carries (`avatar_url`, `picture`, `profilePicture`). */
export function resolveAvatarUrl(source: {
  avatar_url?: string | null;
  picture?: string | null;
  profilePicture?: string | null;
}): string | undefined {
  return (
    source.avatar_url?.trim() ||
    source.picture?.trim() ||
    source.profilePicture?.trim() ||
    undefined
  );
}

export function adaptPatientProfile(
  profile: PatientProfile,
  outputs?: ProducerOutputs,
): PatientRecord {
  logPatientWire('adapt patient profile input', profile);

  const displayName = resolveProfileName(profile);
  const { first, last } = splitName(displayName);
  const nextAppt = profile.appointment?.next;
  const financial = profile.financial;
  const avatarUrl = resolveAvatarUrl(profile);

  return {
    id: String(profile.id),
    name: displayName,
    first,
    last,
    initials: profile.initials?.trim() || initials(displayName),
    ...(avatarUrl ? { avatarUrl } : {}),
    age: profile.age ?? 0,
    since: profile.patient_since ? formatDate(profile.patient_since).split(',')[1]?.trim() ?? profile.patient_since : '',
    visits: profile.appointment?.total_visits ?? 0,
    phone: profile.phone ?? '',
    email: profile.email ?? '',
    lang: 'English',
    status: profile.status ?? 'Active',
    doctorNote:
      outputs?.doctor_note_summary?.summary_text ?? profile.doctor_note_summary ?? '',
    lastInteraction:
      outputs?.last_interaction_summary?.summary_text ?? profile.last_interaction_summary ?? '',
    family: adaptFamilyMembers(profile.family),
    comfort: adaptComfort(profile.comfort),
    starters: resolveConversationStarters(profile, outputs),
    cultural: normalizeWireLabels(profile.cultural_notes),
    appt: {
      when: profile.next_appt
        ? formatDate(profile.next_appt)
        : nextAppt?.date
          ? formatDate(nextAppt.date)
          : '',
      proc: formatProcedureField(nextAppt?.procedure),
      prov: formatProviderField(nextAppt?.provider),
      op: '',
    },
    clinical: {
      allergies: normalizeWireLabels(profile.clinical?.allergies),
      conditions: normalizeWireLabels(profile.clinical?.conditions),
      medications: normalizeWireLabels(profile.clinical?.medications),
      mindful: normalizeWireLabels(
        profile.clinical_mindful_of
          ?? profile.clinical?.be_mindful_of
          ?? profile.clinical?.be_mindful,
      ),
    },
    financial: financial
      ? [
          ['Insurance', normalizeWireLabel(financial.insurance_primary) ?? ''],
          ['Outstanding balance', formatMoney(financial.balance_cents)],
          ['Remaining benefits', formatMoney(financial.remaining_benefits_cents)],
          ['Plan value', formatMoney(financial.plan_value_cents)],
        ]
      : [],
    history: adaptVisitHistory(profile),
  };
}

function mapLang(lang: string): string {
  if (lang === 'en') return 'English';
  if (lang === 'es') return 'Spanish';
  return lang;
}

function mapInsurance(val: boolean | string): boolean {
  if (typeof val === 'boolean') return val;
  return Boolean(val && val !== 'none');
}

function mapForms(val: boolean | string): boolean {
  if (typeof val === 'boolean') return val;
  return val === 'complete' || val === 'completed';
}

function toRosterPatient(item: PatientRosterRecord): PtDirectoryPatient {
  return {
    id: String(item.id),
    profileId: String(item.id),
    title: item.title ?? '',
    first: item.first,
    last: item.last,
    initials: item.initials,
    sex: 'f',
    photo: resolveAvatarUrl(item) ?? '',
    apptTime: item.apptTime,
    provider: item.provider,
    procedure: item.procedure,
    phone: item.phone,
    email: item.email,
    lang: mapLang(item.lang),
    confirmed: item.confirmed,
    insurance: mapInsurance(item.insurance),
    forms: mapForms(item.forms),
    premedicate: item.premedicate,
    balance: item.balance,
    todayValue: item.todayValue,
    planValue: item.planValue,
    alert: item.alert,
    newPatient: item.newPatient,
    nonEnglish: item.nonEnglish,
    triageItems: item.triageItems,
    triageReason: item.triageReason,
  };
}

function formatPatientDisplayName(record: Pick<PatientRosterRecord, 'title' | 'first' | 'last'>): string {
  const parts = [record.title, record.first, record.last].filter((part) => part?.trim());
  return parts.join(' ').trim() || 'Unknown patient';
}

function formatPatientSearchDetail(result: PatientSearchResult): string {
  if (result.dob_masked.trim()) return `DOB ${result.dob_masked}`;
  if (result.phone?.trim()) return result.phone;
  if (result.email?.trim()) return result.email;
  return '';
}

export function normalizePatientSearchResult(record: PatientRosterRecord): PatientSearchResult {
  return {
    id: Number(record.id),
    name: formatPatientDisplayName(record),
    dob_masked: '',
    phone: record.phone,
    email: record.email,
    next_appt: record.apptTime?.trim() ? record.apptTime : null,
    flags: record.flags ?? [],
  };
}

export function normalizePatientSearchResults(data: PatientsSearchResponse | undefined): PatientSearchResult[] {
  if (!data || !('results' in data) || !Array.isArray(data.results)) return [];
  return data.results.map(normalizePatientSearchResult);
}

export function formatPatientSearchSubtitle(result: PatientSearchResult): string {
  return formatPatientSearchDetail(result);
}

export function adaptPatientRoster(data: PatientsSearchResponse | undefined): {
  today: PtDirectoryPatient[];
  tomorrow: PtDirectoryPatient[];
  summary?: PatientRosterSummary;
} {
  if (!data) return { today: [], tomorrow: [] };

  if ('today' in data && Array.isArray(data.today)) {
    const adapted = {
      today: data.today.map(toRosterPatient),
      tomorrow: (data.tomorrow ?? []).map(toRosterPatient),
    };
    return data.summary ? { ...adapted, summary: data.summary } : adapted;
  }

  if ('results' in data && Array.isArray(data.results)) {
    return adaptPatientSearchResults(data.results);
  }

  return { today: [], tomorrow: [] };
}

export function adaptPatientSearchResults(results: PatientRosterRecord[]): {
  today: PtDirectoryPatient[];
  tomorrow: PtDirectoryPatient[];
} {
  return {
    today: results.map(toRosterPatient),
    tomorrow: [],
  };
}

const KNOWN_PC_CHANNELS = new Set<PcChannel>(['email', 'sms', 'call', 'survey', 'recall', 'reminder']);
const KNOWN_PC_STATUSES = new Set<PcCommStatus>([
  'delivered',
  'read',
  'clicked',
  'replied',
  'confirmed',
  'completed',
  'booked',
  'answered',
  'ai_handled',
  'no_answer',
  'voicemail',
]);

function resolvePcChannel(channel: string): PcChannel {
  return KNOWN_PC_CHANNELS.has(channel as PcChannel) ? (channel as PcChannel) : 'sms';
}

function resolvePcStatuses(status: string[] | undefined): PcCommStatus[] {
  return (status ?? []).filter((s): s is PcCommStatus => KNOWN_PC_STATUSES.has(s as PcCommStatus));
}

/** This week / This month / Earlier, relative to now — the wire's `occurred_at` is the source of truth. */
function resolvePcGroup(iso: string | null, now = new Date()): PcPatientCommsBundle['communications'][0]['group'] {
  if (!iso) return 'Earlier';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Earlier';
  const days = (now.getTime() - d.getTime()) / (24 * 60 * 60 * 1000);
  if (days < 7) return 'This week';
  if (days < 30) return 'This month';
  return 'Earlier';
}

function adaptCommEventDetail(detail: PatientCommEventDetail | undefined, summary: string): PcCommDetail | undefined {
  if (!detail) return undefined;

  if (detail.kind === 'call') {
    return {
      ...(detail.duration ? { duration: detail.duration } : {}),
      ...(detail.handledBy ? { handledBy: detail.handledBy } : {}),
      aiSummary: detail.transcript ?? summary,
      hasRecording: detail.hasRecording ?? false,
    };
  }

  if (detail.kind === 'sms') {
    const messages = detail.messages ?? [];
    const outbound = [...messages].reverse().find((m) => m.direction === 'out');
    const inbound = [...messages].reverse().find((m) => m.direction === 'in');
    return {
      ...(outbound ? { body: outbound.body } : {}),
      ...(inbound ? { reply: { text: inbound.body, time: inbound.time } } : {}),
    };
  }

  if (detail.kind === 'email') {
    const lastMessage = detail.messages?.[detail.messages.length - 1];
    return {
      ...(detail.subject ? { subject: detail.subject } : {}),
      ...(lastMessage ? { body: lastMessage.body } : {}),
    };
  }

  if (detail.kind === 'recall') {
    return {
      ...(detail.templateRef ? { campaign: detail.templateRef } : {}),
      touchpoints: (detail.touchpoints ?? []).map((tp) => ({
        ch: resolvePcChannel(tp.ch),
        note: tp.note,
        result: tp.result,
        time: tp.time,
      })),
    };
  }

  return undefined;
}

export function adaptPatientComms(
  response: PatientCommsResponse | undefined,
  outputs?: ProducerOutputs,
): PcPatientCommsBundle {
  const events = response?.events ?? [];

  return {
    aiSummary: response?.summary || outputs?.last_interaction_summary?.summary_text || '',
    stats: (response?.stats ?? []).map((s) => ({
      channel: resolvePcChannel(s.channel),
      count: s.count,
      sub: 'last 12 months',
    })),
    ...(response?.header
      ? {
          meta: {
            pronouns: response.header.pronouns || '—',
            preferredContact: response.header.preferred_contact || 'Text',
          },
        }
      : {}),
    communications: events.map((e) => {
      const channel = resolvePcChannel(e.channel);
      return {
        id: String(e.id),
        channel,
        direction: e.direction === 'inbound' ? 'in' : 'out',
        group: resolvePcGroup(e.occurred_at),
        title: e.subject ?? e.summary,
        time: formatDate(e.occurred_at ?? undefined),
        preview: e.summary,
        status: resolvePcStatuses(e.status),
        ...(e.outcome ? { outcome: e.outcome } : {}),
        ...(() => {
          const detail = adaptCommEventDetail(e.detail, e.summary);
          return detail ? { detail } : {};
        })(),
      };
    }),
  };
}

const V2_CHANNELS = new Set<PcV2Channel>(['email', 'sms', 'call']);

function asV2Channel(value: string | undefined): PcV2Channel | null {
  return value !== undefined && V2_CHANNELS.has(value as PcV2Channel) ? (value as PcV2Channel) : null;
}

/** Recalls and reminders are reasons, not channels — show the text, call, or email they were. */
function resolveV2Delivery(event: PatientCommEvent): PcV2Channel | null {
  if (event.channel === 'survey') return null;
  if (event.channel === 'recall' && event.detail?.kind === 'recall') {
    return asV2Channel(event.detail.touchpoints?.[0]?.ch);
  }
  if (event.channel === 'reminder') {
    return event.detail && event.detail.kind !== 'recall' ? asV2Channel(event.detail.kind) : 'sms';
  }
  return asV2Channel(event.channel);
}

function v2Summary(event: PatientCommEvent, channel: PcV2Channel): string {
  if (channel === 'email') return event.subject?.trim() || event.summary;
  if (channel === 'sms' && event.detail?.kind === 'sms') {
    const body = event.detail.messages?.[event.detail.messages.length - 1]?.body;
    if (body) return body;
  }
  return event.summary;
}

/** Read-only history for the v2 communications tab. Inbound email stays in the Email inbox. */
export function adaptPatientCommsV2(response: PatientCommsResponse | undefined): PcV2Bundle {
  const entries = (response?.events ?? []).flatMap((event) => {
    const channel = resolveV2Delivery(event);
    if (!channel) return [];
    const direction: 'in' | 'out' = event.direction === 'inbound' ? 'in' : 'out';
    if (channel === 'email' && direction === 'in') return [];
    return [
      {
        id: String(event.id),
        channel,
        direction,
        at: event.occurred_at ?? '',
        summary: v2Summary(event, channel),
      },
    ];
  });
  return { entries };
}

function emptyInsurancePlan(): PiPatientInsuranceBundle['primary'] {
  return {
    role: 'Primary',
    carrier: '',
    plan: '',
    type: '',
    network: '',
    memberId: '',
    group: '',
    groupName: '',
    subscriber: '',
    relationship: '',
    effective: '',
    planYear: '',
    daysLeft: 0,
    phone: '',
    accent: 'var(--sage-0)',
    benefits: {
      annualMax: 0,
      used: 0,
      remaining: 0,
      deductible: 0,
      deductibleMet: 0,
      familyDed: 0,
      familyMet: 0,
    },
    verifiedLabel: '',
  };
}

export function adaptPatientInsurance(
  response: InsuranceResponse | undefined,
  claims?: ClaimsResponse,
): PiPatientInsuranceBundle {
  const primaryPlan = response?.plans?.find((p) => p.role === 'primary') ?? response?.plans?.[0];
  const secondaryPlan = response?.plans?.find((p) => p.role === 'secondary') ?? null;

  const mapPlan = (plan: NonNullable<InsuranceResponse['plans']>[0]): PiPatientInsuranceBundle['primary'] => ({
    role: plan.role === 'secondary' ? 'Secondary' : 'Primary',
    carrier: plan.carrier,
    plan: plan.plan,
    type: plan.type,
    network: plan.network,
    memberId: plan.memberId,
    group: plan.group,
    groupName: plan.group,
    subscriber: '',
    relationship: plan.relationship,
    effective: '',
    planYear: plan.planYear,
    daysLeft: plan.daysLeft,
    phone: '',
    accent: 'var(--sage-0)',
    benefits: {
      annualMax: plan.benefits.annualMax,
      used: plan.benefits.used,
      remaining: plan.benefits.remaining,
      deductible: plan.benefits.deductible,
      deductibleMet: plan.benefits.deductibleMet,
      familyDed: 0,
      familyMet: 0,
    },
    verifiedLabel: plan.verifiedAt ? `Verified ${formatDate(plan.verifiedAt)}` : 'Not verified',
  });

  return {
    primary: primaryPlan ? mapPlan(primaryPlan) : emptyInsurancePlan(),
    secondary: secondaryPlan ? mapPlan(secondaryPlan) : null,
    verification: {
      primaryLabel: response?.verification?.status ?? 'unknown',
      primaryMeta: response?.verification?.checkedAt ? formatDate(response.verification.checkedAt) : '',
      alertHtml: '',
    },
    coverage: (primaryPlan?.coverageByCategory ?? []).map((c) => ({
      cat: c.category,
      pct: `${c.percent}%`,
      desc: '',
      tone: 'neutral' as const,
    })),
    limitations: (primaryPlan?.limitations ?? []).map((l) => [l.type, l.text] as [string, string]),
    preauth: [],
    frequency: [],
    treatmentPlan: { total: 0, insurance: 0, patient: 0, rows: [] },
    claims: (claims?.claims ?? []).map((c) => ({
      date: formatDate(c.service_date),
      id: c.external_id,
      proc: c.lines?.[0]?.cdt ?? '',
      billed: c.total_charges_cents / 100,
      allowed: c.lines?.[0]?.allowed_cents != null ? c.lines[0].allowed_cents / 100 : null,
      ins: c.lines?.[0]?.insurance_paid_cents != null ? c.lines[0].insurance_paid_cents / 100 : null,
      pat: c.lines?.[0]?.patient_responsibility_cents != null ? c.lines[0].patient_responsibility_cents / 100 : null,
      status: c.claim_state,
      tone: 'neutral' as const,
    })),
  };
}

export function adaptPatientTreatment(
  cheatsheet: TpiCheatsheet | undefined,
  outputs?: ProducerOutputs,
): TpPatientTreatmentBundle {
  const treatments = (cheatsheet?.recommended ?? []).map((item, i) => ({
    id: `t-${i}`,
    order: i === 0 ? ('lead' as TpPatientTreatmentBundle['treatments'][0]['order']) : ('then' as TpPatientTreatmentBundle['treatments'][0]['order']),
    code: item.cdt,
    tooth: item.tooth,
    description: item.name,
    fee: Math.round(item.fee_cents / 100),
    insurance: Math.round(item.ins_estimate_cents / 100),
    patientOOP: Math.round(item.patient_estimate_cents / 100),
    diagnosedAt: '',
    declineHistory: [],
    whyThisPosition: item.clinical_reason,
    howToFrame: item.cost_of_delay,
    clinicalNow: item.clinical_reason,
  }));

  return {
    context: {
      unscheduledValue: Math.round((cheatsheet?.plan_total?.fee_cents ?? 0) / 100),
      insurance: {
        carrier: '',
        annualMax: 0,
        used: 0,
        remaining: 0,
        resetsIn: '',
      },
      nextVisit: { date: '', time: '', reason: '' },
      tenureYears: 0,
    },
    approach: {
      lead: outputs?.next_best_action?.action ?? '',
      tail: '',
      body: outputs?.next_best_action?.reason ?? '',
      historicalPattern: '',
    },
    quickRead: (cheatsheet?.signals ?? []).map((s) => ({
      label: s.label,
      value: s.evidence,
      tone: 'neutral' as const,
    })),
    treatments,
    objections: [],
    worked: [],
    didntWork: [],
    patternTakeaway: '',
  };
}

const KNOWN_ASSIGNMENT_STATUSES = new Set<PfAssignmentStatus>([
  'not_sent',
  'pending',
  'in_progress',
  'submitted',
  'approved',
  'incomplete',
  'cancelled',
  'expired',
]);

const AWAITING_PATIENT_STATUSES = new Set(['pending', 'sent', 'in_progress', 'not_sent']);

function mapAssignmentStatus(status: string, submittedAt?: string | null): PfAssignmentStatus {
  // Backend keeps `pending` after the patient submits (it means "awaiting staff");
  // a stamped submittedAt is what tells us the patient finished.
  if (submittedAt && AWAITING_PATIENT_STATUSES.has(status)) return 'submitted';
  if (KNOWN_ASSIGNMENT_STATUSES.has(status as PfAssignmentStatus)) {
    return status as PfAssignmentStatus;
  }
  if (status === 'completed') return 'approved';
  if (status === 'sent') return 'pending';
  if (status === 'rejected') return 'incomplete';
  return 'not_sent';
}

function pickWireId(...values: Array<string | undefined | null>): string | undefined {
  for (const value of values) {
    const trimmed = value?.trim();
    if (trimmed) return trimmed;
  }
  return undefined;
}

function extractTokenFromPatientUrl(url: string | undefined): string | undefined {
  if (!url?.trim()) return undefined;
  const patterns = [
    /\/patient\/forms\/([^/?#]+)/i,
    /\/forms\/patient\/([^/?#]+)/i,
    /\/public\/forms\/([^/?#]+)/i,
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match?.[1]?.trim()) return match[1].trim();
  }
  return undefined;
}

/**
 * Sync status isn't on the per-patient forms endpoint — only the org-wide
 * `/api/forms/queue` reports it. Callers that have that data pass it in, keyed
 * by assignment id (falling back to submission id); forms with no match (not
 * yet submitted, or not present in the queue) read as `not_started`.
 */
export function adaptPatientForms(
  response: PatientFormsResponse | undefined,
  syncStatusByKey?: Record<string, PfSyncStatus>,
): PfPatientFormsBundle {
  if (!response) {
    return {
      aiSummary: '',
      forms: [],
      stats: { completed: 0, open: 0, needsReview: 0, total: 0 },
    };
  }

  const forms = (response.forms ?? []).map((form) => {
    const submissionId = pickWireId(form.submission_id, form.submissionId);
    const assignmentId = pickWireId(form.assignment_id, form.assignmentId);
    const accessToken = pickWireId(
      form.access_token,
      form.accessToken,
      form.token,
      form.patient_token,
      extractTokenFromPatientUrl(form.patient_url ?? form.patientUrl),
    );
    const name = form.name ?? (form as { form_name?: string; formName?: string }).form_name
      ?? (form as { formName?: string }).formName
      ?? 'Form';
    const desc = form.desc ?? (form as { description?: string }).description ?? '';

    const sentAt = form.sentAt ?? form.sent_at;
    // Backend keeps this field's original meaning: approvedAt ?? submittedAt —
    // the moment a staff member closed the form.
    const completedAt = form.completedAt ?? form.completed_at;

    const syncKey = assignmentId ?? submissionId;
    const syncStatus: PfSyncStatus = (syncKey ? syncStatusByKey?.[syncKey] : undefined) ?? 'not_started';

    return {
      id: form.id,
      name,
      desc,
      assignmentStatus: mapAssignmentStatus(form.status, form.submittedAt ?? form.submitted_at),
      syncStatus,
      timeline: {
        ...(sentAt ? { sentAt: formatDate(sentAt) } : {}),
        ...(completedAt ? { closedAt: formatDate(completedAt) } : {}),
      },
      ...(submissionId ? { submissionId } : {}),
      ...(assignmentId ? { assignmentId } : {}),
      ...(accessToken ? { accessToken } : {}),
      ...(form.channel ? { channel: form.channel } : {}),
    };
  });

  const bundle = {
    aiSummary: response.aiSummary ?? response.ai_summary ?? '',
    stats: pfFormsStats(forms),
    forms,
  };
  logPatientWire('adapted patient forms', bundle);
  return bundle;
}
